import { ChevronUp, ChevronDown, Checkmark } from '@carbon/icons-react';
import { CustomizedTypography, Stack } from '@components/Customized';
import { Image } from '@components/Image';
import { clsx, colorSet } from '@components/Provider';
import RequiredDot from '@components/RequiredDot';
import { CapaBankCode } from '@generated/graphql';
import {
  Select,
  MenuItem,
  TextField,
  makeStyles,
  MenuProps
} from '@material-ui/core';
import { useState } from 'react';
import {
  Control,
  Controller,
  DeepMap,
  FieldError,
  FieldValues
} from 'react-hook-form';
import * as bankLogo from './logo';

interface BankAccountInputGroupProps {
  control: Control<FieldValues>;
  errors: DeepMap<FieldValues, FieldError>;
}

const BankAccountInputGroup = ({
  control,
  errors
}: BankAccountInputGroupProps) => {
  const {
    arrowIcon,
    preventOpacity,
    bankList,
    label,
    menuItem,
    textField,
    inputWrapper
  } = useStyle();
  const [selectedBankForDisplay, setSelectedBankForDisplay] = useState<
    CapaBankCode
  >();

  const { bank: bankError, bankAccount: bankAccountError } = errors;

  const isBankError = !!bankError;
  const isBankAccountError = !!bankAccountError;

  const getIconComponent = (iconElement: { className: string }) => {
    const isOpen = iconElement.className.includes('MuiSelect-iconOpen');

    return isOpen ? (
      <ChevronUp size={24} className={arrowIcon} />
    ) : (
      <ChevronDown size={24} className={arrowIcon} />
    );
  };

  return (
    <>
      <label className={clsx(label, 'bank-select')}>
        <Stack gap={4}>
          <CustomizedTypography variant="body1" color="textPrimary">
            환불계좌 은행 <RequiredDot />
          </CustomizedTypography>
        </Stack>
        <Controller
          defaultValue={'은행선택'}
          control={control}
          name="bank"
          rules={{
            required: true,
            validate: (value) => (value !== '은행선택' ? true : '입력해주세요')
          }}
          render={(render) => (
            <div className={inputWrapper}>
              <Select
                {...render}
                data-testid="refundAccountForm-bank"
                error={isBankError}
                variant="outlined"
                className={bankList}
                MenuProps={CustomMenuProps}
                IconComponent={getIconComponent}
                onChange={(e) => {
                  const selectedBank = e.target.value as CapaBankCode;
                  setSelectedBankForDisplay(selectedBank);
                  render.onChange(selectedBank);
                }}
                renderValue={(selectedValue) => {
                  return (
                    <CustomizedTypography variant="body1">
                      {CapaBankCodeToBankInfo[selectedValue as CapaBankCode]
                        ?.name ?? '은행선택'}
                    </CustomizedTypography>
                  );
                }}
              >
                <MenuItem
                  className={clsx(preventOpacity, menuItem)}
                  value={'은행선택'}
                  disabled
                >
                  <CustomizedTypography variant="body2" fontWeight="500">
                    은행선택
                  </CustomizedTypography>
                </MenuItem>
                {Object.values(CapaBankCode).map((bankValue) => {
                  const { name: bankName, logo } = CapaBankCodeToBankInfo[
                    bankValue
                  ] ?? {
                    name: bankValue,
                    logo: null
                  };

                  if (!bankName) {
                    return null;
                  }

                  return (
                    <MenuItem
                      className={menuItem}
                      key={bankName}
                      value={bankValue}
                    >
                      <Stack gap={8}>
                        {logo && (
                          <Image
                            className="bank-logo"
                            src={logo}
                            alt={`${bankName} 로고`}
                          />
                        )}
                        <CustomizedTypography variant="body2" fontWeight="500">
                          {bankName}
                        </CustomizedTypography>
                      </Stack>

                      {selectedBankForDisplay === bankValue && (
                        <Checkmark color={colorSet.primary500} size={20} />
                      )}
                    </MenuItem>
                  );
                })}
              </Select>
              {isBankError && (
                <CustomizedTypography variant="caption" color="error">
                  {bankError.message}
                </CustomizedTypography>
              )}
            </div>
          )}
        />
      </label>
      <label className={label}>
        <Stack gap={4}>
          <CustomizedTypography variant="body1" color="textPrimary">
            계좌번호 <RequiredDot />
          </CustomizedTypography>
        </Stack>
        <Controller
          defaultValue={''}
          control={control}
          name="bankAccount"
          rules={{ required: { value: true, message: '입력해주세요' } }}
          render={(render) => (
            <div className={inputWrapper}>
              <TextField
                data-testid="refundAccountForm-account"
                {...render}
                className={textField}
                error={isBankAccountError}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  render.onChange(value);
                  return value;
                }}
                onWheel={(e) => {
                  (e.target as HTMLInputElement).blur();
                }}
                variant="outlined"
                placeholder="숫자만 입력"
              />
              {isBankAccountError && (
                <CustomizedTypography variant="caption" color="error">
                  {bankAccountError.message}
                </CustomizedTypography>
              )}
            </div>
          )}
        />
      </label>
    </>
  );
};

export default BankAccountInputGroup;

const useStyle = makeStyles(
  (theme) => ({
    arrowIcon: {
      top: 'calc(50% - 12px)',
      right: 12,
      color: colorSet.gray700,
      position: 'absolute',
      pointerEvents: 'none'
    },
    preventOpacity: {
      opacity: `1 !important`
    },
    bankList: {
      width: '100%',
      '& .MuiOutlinedInput-input': {
        padding: `12px 14px`
      }
    },
    label: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      width: '100%',
      '&.bank-select': {
        width: 331
      },
      [theme.breakpoints.down('md')]: {
        '&.bank-select': {
          width: '100%'
        }
      }
    },
    menuItem: {
      opacity: `1 !important`,
      justifyContent: 'space-between',
      '& .bank-logo': {
        width: 20,
        height: 20
      },
      '&.MuiListItem-root.Mui-selected': {
        backgroundColor: colorSet.gray000
      }
    },
    textField: {
      width: '100%',
      '& .MuiOutlinedInput-root': {
        height: 48
      }
    },
    inputWrapper: {
      width: '100%',
      '& .MuiTypography-colorError': {
        position: 'relative',
        left: 2
      }
    }
  }),
  { classNamePrefix: 'BankAccountInputGroup-' }
);

const CustomMenuProps: Partial<MenuProps> = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left'
  },
  getContentAnchorEl: null,
  PaperProps: {
    elevation: 0,
    style: {
      border: `${colorSet.gray400} 1px solid`,
      borderRadius: 8,
      maxHeight: 248,
      transform: 'translateY(6px)'
    }
  }
};

export const CapaBankCodeToBankInfo: Partial<Record<
  CapaBankCode,
  { name: string | null; logo: string | null }
>> = {
  [CapaBankCode.BusanBank]: { name: '부산', logo: bankLogo.부산 },
  [CapaBankCode.HanaSeoulBank]: { name: '하나', logo: bankLogo.하나 },
  [CapaBankCode.KbBank]: { name: 'KB국민', logo: bankLogo.KB국민 },
  [CapaBankCode.DaeguBank]: { name: '대구', logo: bankLogo.대구 },
  [CapaBankCode.Ibk]: { name: 'IBK기업', logo: bankLogo.IBK기업 },
  [CapaBankCode.JeonbukBank]: { name: '전북', logo: bankLogo.전북 },
  [CapaBankCode.KBank]: { name: '케이뱅크', logo: bankLogo.케이뱅크 },
  [CapaBankCode.KakaoBank]: { name: '카카오뱅크', logo: bankLogo.카카오뱅크 },
  [CapaBankCode.Kdb]: { name: 'KDB산업', logo: bankLogo.KDB산업 },
  [CapaBankCode.Keb]: { name: null, logo: null },
  [CapaBankCode.Kfcc]: { name: '새마을금고', logo: bankLogo.새마을금고 },
  [CapaBankCode.KwangjuBank]: { name: '광주', logo: bankLogo.광주 },
  [CapaBankCode.KyongnamBank]: { name: '경남', logo: bankLogo.경남 },
  [CapaBankCode.NhBank]: { name: '농협', logo: bankLogo.NH농협 },
  [CapaBankCode.PostBank]: { name: '우체국', logo: bankLogo.우체국 },
  [CapaBankCode.ScBank]: { name: 'SC제일', logo: bankLogo.SC제일 },
  [CapaBankCode.ShBank]: { name: '수협', logo: bankLogo.수협 },
  [CapaBankCode.ShinhanBank]: { name: '신한', logo: bankLogo.신한 },
  [CapaBankCode.TossBank]: { name: '토스', logo: bankLogo.토스뱅크 },
  [CapaBankCode.WooriBank]: { name: '우리', logo: bankLogo.우리 },
  [CapaBankCode.CreditUnionBank]: { name: '신협', logo: bankLogo.신협 },
  [CapaBankCode.CityHanmiBank]: { name: '씨티', logo: bankLogo.씨티 }
};
